import React, { useState, useEffect } from 'react';
import userService from '../services/userService';
import { COMPANY_ID_MAPPINGS } from '../constants/companyIds';
import UpdateNotification from '../components/UpdateNotification';
import { 
  UserPlusIcon, 
  TrashIcon, 
  ShieldCheckIcon,
  UserGroupIcon,
  EnvelopeIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ 
    name: '', 
    email: '', 
    username: '', 
    password: '', 
    role: 'user', 
    allowedOsTypes: 'Todas' 
  });
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'info' });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    const loadedUsers = userService.getUsers();
    setUsers(loadedUsers);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.password) {
      setToast({ message: 'Por favor, preencha todos os campos obrigatórios.', type: 'warning' });
      return;
    }

    setIsLoading(true);
    try {
      // Obter IDs das empresas baseado na permissão selecionada
      let allowedCompanies = [];
      if (newUser.allowedOsTypes === 'Todas') {
        // Todas as empresas de todos os grupos
        allowedCompanies = [
          ...COMPANY_ID_MAPPINGS['Cia. Zaffari'],
          ...COMPANY_ID_MAPPINGS['Comercial Zaffari'],
          ...COMPANY_ID_MAPPINGS['Stok Center']
        ];
      } else {
        allowedCompanies = COMPANY_ID_MAPPINGS[newUser.allowedOsTypes] || [];
      }

      // Mapear os dados para o formato esperado pela API
      const payload = {
        name: newUser.name,
        email: newUser.email,
        username: newUser.username || newUser.email.split('@')[0],
        password: newUser.password,
        role: newUser.role,
        allowedCompanies: allowedCompanies,
        // Mantemos allowedOsTypes para compatibilidade local se necessário
        allowedOsTypes: newUser.allowedOsTypes
      };

      const result = await userService.register(payload);
      if (result.success) {
        setToast({ message: 'Usuário cadastrado com sucesso!', type: 'success' });
        setNewUser({ 
          name: '', 
          email: '', 
          username: '', 
          password: '', 
          role: 'user', 
          allowedOsTypes: 'Todas' 
        });
        loadUsers();
      } else {
        setToast({ message: result.error || 'Erro ao cadastrar usuário.', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Erro ao cadastrar usuário.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = (id) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      userService.deleteUser(id);
      setToast({ message: 'Usuário excluído com sucesso.', type: 'success' });
      loadUsers();
    }
  };

  const handlePermissionChange = (id, permission) => {
    const result = userService.updateUserPermissions(id, permission);
    
    // Se for o próprio usuário logado, atualiza o perfil no localStorage para refletir na visualização
    const currentUserJson = localStorage.getItem('auth_user_profile');
    if (currentUserJson && result.success) {
      const currentUser = JSON.parse(currentUserJson);
      const updatedUser = result.user;
      
      // Se for o próprio usuário logado, atualiza o perfil
      if (currentUser.email === updatedUser.email || currentUser.id === id) {
        currentUser.allowedOsTypes = updatedUser.allowedOsTypes;
        currentUser.allowedCompanies = updatedUser.allowedCompanies;
        localStorage.setItem('auth_user_profile', JSON.stringify(currentUser));
        setToast({ message: 'Permissões atualizadas. Recarregue a página para aplicar os novos filtros de dados.', type: 'success' });
      } else {
        setToast({ message: 'Permissões atualizadas.', type: 'success' });
      }
    } else if (result.success) {
      setToast({ message: 'Permissões atualizadas.', type: 'success' });
    }
    
    loadUsers();
  };

  return (
    <div className="space-y-6">
      {toast.message && (
        <UpdateNotification
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'info' })}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-dark-text flex items-center gap-2">
            <UserGroupIcon className="w-8 h-8 text-primary" />
            Gerenciamento de Usuários
          </h2>
          <p className="text-dark-text-secondary text-sm mt-1">
            Cadastre novos usuários e gerencie permissões de visualização.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulário de Cadastro */}
        <div className="lg:col-span-1">
          <div className="card-dark p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-dark-text mb-4 flex items-center gap-2">
              <UserPlusIcon className="w-5 h-5 text-primary" />
              Novo Usuário
            </h3>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Nome Completo</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <IdentificationIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={newUser.name}
                    onChange={handleInputChange}
                    className="input-dark w-full pl-10"
                    placeholder="Ex: João Silva"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Nome de Usuário</label>
                <input
                  type="text"
                  name="username"
                  value={newUser.username}
                  onChange={handleInputChange}
                  className="input-dark w-full"
                  placeholder="Ex: joao.silva"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">E-mail</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={newUser.email}
                    onChange={handleInputChange}
                    className="input-dark w-full pl-10"
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Perfil de Acesso</label>
                <select
                  name="role"
                  value={newUser.role}
                  onChange={handleInputChange}
                  className="input-dark w-full"
                >
                  <option value="user">Usuário Comum</option>
                  <option value="admin">Administrador</option>
                  <option value="master">Master</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Senha Temporária</label>
                <input
                  type="password"
                  name="password"
                  value={newUser.password}
                  onChange={handleInputChange}
                  className="input-dark w-full"
                  placeholder="******"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Permissão de Visualização</label>
                <select
                  name="allowedOsTypes"
                  value={newUser.allowedOsTypes}
                  onChange={handleInputChange}
                  className="input-dark w-full"
                >
                  <option value="Todas">Todas as Ordens</option>
                  <option value="Cia. Zaffari">Cia. Zaffari apenas</option>
                  <option value="Comercial Zaffari">Comercial Zaffari apenas</option>
                  <option value="Stok Center">Stok Center apenas</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <UserPlusIcon className="w-5 h-5" />
                    Cadastrar Usuário
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Lista de Usuários */}
        <div className="lg:col-span-2">
          <div className="card-dark overflow-hidden">
            <div className="p-4 border-b border-gray-700 bg-gray-800/50">
              <h3 className="text-lg font-semibold text-dark-text">Usuários Cadastrados</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="table-dark">
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>E-mail</th>
                    <th>Visualização</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium">{user.name}</span>
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <select
                            value={user.allowedOsTypes || 'Todas'}
                            onChange={(e) => handlePermissionChange(user.id, e.target.value)}
                            className="bg-gray-700 text-xs text-dark-text border border-gray-600 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                          >
                            <option value="Todas">Todas</option>
                            <option value="Cia. Zaffari">Cia. Zaffari</option>
                            <option value="Comercial Zaffari">Comercial Zaffari</option>
                            <option value="Stok Center">Stok Center</option>
                          </select>
                        </td>
                        <td className="text-center">
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-2 text-danger hover:bg-danger/10 rounded-full transition-colors"
                            title="Excluir Usuário"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-8 text-dark-text-secondary">
                        Nenhum usuário cadastrado além de você.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
